import { ADULT_AGE_YEARS, ageInYears, type FamilyRelation, type GuardianRelation } from '@his/shared';

export interface LinkedPatient {
  fullName: string;
  phone: string | null;
  dob?: string | null;
  approximateAgeYears?: number | null;
}

export function mayBeGuardian(picked: LinkedPatient): boolean {
  const age = ageInYears(picked.dob ? picked.dob.slice(0, 10) : null, picked.approximateAgeYears ?? null);
  return age === null || age >= ADULT_AGE_YEARS;
}

export interface LinkDefaultsInput {
  dob?: string | null;
  approximateAgeYears?: number | null;
  phone?: string | null;
  guardianName?: string | null;
  guardianPhone?: string | null;
}

export interface LinkDefaults {
  phone?: string;
  guardianRelation?: GuardianRelation;
  guardianName?: string;
  guardianPhone?: string;
}

const GUARDIAN_RELATION_OF: Record<FamilyRelation, GuardianRelation> = {
  mother: 'parent',
  father: 'parent',
  parent: 'parent',
  child: 'child',
  spouse: 'spouse',
  sibling: 'sibling',
  other: 'other',
};

function blank(value: string | null | undefined): boolean {
  return !value || value.trim().length === 0;
}

export function linkedPatientDefaults(values: LinkDefaultsInput, picked: LinkedPatient, relation: FamilyRelation, previous?: LinkedPatient | null): LinkDefaults {
  const out: LinkDefaults = {};
  if ((blank(values.phone) || values.phone === previous?.phone) && picked.phone) out.phone = picked.phone;
  const age = ageInYears(values.dob ?? null, values.approximateAgeYears ?? null);
  const minor = age !== null && age < ADULT_AGE_YEARS;
  if (relation === 'child' || !minor || !mayBeGuardian(picked)) return out;
  const filledByLink = blank(values.guardianName) || sameName(values.guardianName, picked.fullName) || sameName(values.guardianName, previous?.fullName);
  if (!filledByLink) return out;
  out.guardianRelation = GUARDIAN_RELATION_OF[relation];
  out.guardianName = picked.fullName;
  const phoneFilledByLink = blank(values.guardianPhone) || values.guardianPhone === previous?.phone;
  if (phoneFilledByLink && picked.phone) out.guardianPhone = picked.phone;
  return out;
}

function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const fold = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');
  return fold(a) === fold(b);
}
